import { useState, useRef, useEffect } from "react";
import api from "@/lib/api";
import { Sparkles, Send, Loader2 } from "lucide-react";

const SUGGESTIONS = [
  "How many cases are in each status?",
  "Draft a 'Filed' update email for my latest client",
  "Summarize my newest intake",
];

export default function AdminAssistant() {
  const [messages, setMessages] = useState([
    { role: "assistant", content: "Hi! I'm your ops assistant. Ask about your cases, draft client emails, or summarize an intake." },
  ]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const scrollRef = useRef(null);
  const sid = useRef(`panel_${Date.now()}`);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, loading]);

  const send = async (preset) => {
    const text = (preset || input).trim();
    if (!text || loading) return;
    if (!preset) setInput("");
    setMessages((m) => [...m, { role: "user", content: text }]);
    setLoading(true);
    try {
      const { data } = await api.post("/admin/assistant", { session_id: sid.current, message: text });
      setMessages((m) => [...m, { role: "assistant", content: data.reply }]);
    } catch {
      setMessages((m) => [...m, { role: "assistant", content: "Sorry — I couldn't process that. Try again." }]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-white border border-[#E5E5E5] rounded-2xl flex flex-col h-[460px]" data-testid="admin-assistant">
      <div className="px-5 py-4 border-b border-[#E5E5E5] flex items-center gap-2">
        <span className="h-8 w-8 rounded-lg bg-[#114A31] flex items-center justify-center">
          <Sparkles className="h-4 w-4 text-white" />
        </span>
        <span className="font-heading font-semibold text-[#1C1C1C]">Smart Assistant</span>
      </div>

      <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-3" data-testid="assistant-messages">
        {messages.map((m, i) => (
          <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
            <div
              className={`max-w-[85%] rounded-2xl px-3.5 py-2.5 text-sm whitespace-pre-wrap ${
                m.role === "user"
                  ? "bg-[#114A31] text-white rounded-br-sm"
                  : "bg-[#F9F9F8] border border-[#E5E5E5] text-[#1C1C1C] rounded-bl-sm"
              }`}
            >
              {m.content}
            </div>
          </div>
        ))}
        {loading && (
          <div className="flex justify-start">
            <div className="bg-[#F9F9F8] border border-[#E5E5E5] rounded-2xl px-3.5 py-2.5">
              <Loader2 className="h-4 w-4 animate-spin text-[#114A31]" />
            </div>
          </div>
        )}
      </div>

      {messages.length <= 1 && (
        <div className="px-4 pb-2 flex flex-wrap gap-2">
          {SUGGESTIONS.map((s) => (
            <button
              key={s}
              onClick={() => send(s)}
              data-testid="assistant-suggestion"
              className="text-xs bg-[#E6F0EB] text-[#114A31] rounded-full px-3 py-1.5 hover:bg-[#d4e8dd] transition-colors"
            >
              {s}
            </button>
          ))}
        </div>
      )}

      <div className="p-3 border-t border-[#E5E5E5] flex items-center gap-2">
        <input
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && send()}
          placeholder="Ask about cases, draft an email…"
          data-testid="assistant-input"
          className="flex-1 rounded-full border border-[#E5E5E5] px-4 py-2.5 text-sm focus:outline-none focus:border-[#114A31]"
        />
        <button
          onClick={() => send()}
          disabled={loading}
          data-testid="assistant-send-btn"
          className="h-10 w-10 rounded-full bg-[#114A31] hover:bg-[#1A6B46] text-white flex items-center justify-center shrink-0 disabled:opacity-50"
        >
          <Send className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}
